import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, fmtEUR, pct } from "../lib/api";
import SupportModal from "../components/SupportModal";
import { Heart, Instagram, Twitter } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

export default function AthleteProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const [a, setA] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [donations, setDonations] = useState([]);
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportMode, setSupportMode] = useState("personal");
  const [supportFrequency, setSupportFrequency] = useState("one-off");

  const load = async () => {
    const [aRes, uRes, dRes] = await Promise.all([
      api.get(`/athletes/${id}`),
      api.get(`/athletes/${id}/updates`),
      api.get(`/athletes/${id}/donations`),
    ]);
    setA(aRes.data);
    setUpdates(uRes.data);
    setDonations(dRes.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const handleFollow = async () => {
    if (!user) {
      toast("Sign in to follow golfers");
      return;
    }
    try {
      await api.post("/follow", { athlete_id: a.athlete_id });
      toast.success("Follow preference saved");
    } catch {}
  };

  if (!a) {
    return <div className="container-tight py-24 overline" data-testid="athlete-loading">Loading…</div>;
  }
  const progress = pct(a.funding_raised, a.funding_goal);

  return (
    <div data-testid="athlete-profile-page">
      {/* HERO */}
      <section className="border-b border-border" data-testid="athlete-hero">
        <div className="container-tight pt-12 pb-0">
          <div className="overline">{[a.country, a.tour_level].filter(Boolean).join(" · ")}</div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight mt-3">
            {a.name.split(" ")[0]} <span className="text-primary">{a.name.split(" ").slice(1).join(" ")}</span>
          </h1>
        </div>
        <div className="container-tight pt-10 grid grid-cols-1 md:grid-cols-12 gap-10 pb-16">
          <div className="md:col-span-7">
            <div className="aspect-[4/5] overflow-hidden border border-border">
              <img src={a.image_url} alt={a.name} className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="md:col-span-5">
            <dl className="grid grid-cols-2 gap-0 border border-border">
              {[
                ["Age", a.age],
                ["Handicap", a.handicap],
                ["Ranking", a.ranking || "–"],
                ["Tour level", a.tour_level],
                ["Golf club", a.golf_club || "–"],
                ["Golf club / business", a.gi_membership || "–"],
              ].map(([k, v], i) => (
                <div key={k} className={`p-4 ${i % 2 === 0 ? "border-r border-border" : ""} ${i < 4 ? "border-b border-border" : ""}`}>
                  <div className="overline text-foreground/60">{k}</div>
                  <div className="font-display text-2xl mt-1">{v}</div>
                </div>
              ))}
            </dl>

            {/* FUNDING PROGRESS */}
            <div className="mt-8" data-testid="funding-progress">
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="overline">Raised</div>
                  <div className="font-display text-4xl">{fmtEUR(a.funding_raised)}</div>
                </div>
                <div className="text-right">
                  <div className="overline">Goal</div>
                  <div className="font-display text-2xl text-foreground/60">{fmtEUR(a.funding_goal)}</div>
                </div>
              </div>
              <div className="mt-3 h-1 bg-muted relative">
                <div className="absolute left-0 top-0 h-1 bg-primary" style={{ width: `${progress}%` }} />
              </div>
              <div className="mt-1 text-xs text-foreground/60">{progress}% funded</div>
            </div>

            {/* CTAs */}
            <div className="mt-8 grid grid-cols-1 gap-2" data-testid="athlete-ctas">
              <button
                onClick={() => { setSupportMode("personal"); setSupportFrequency("one-off"); setSupportOpen(true); }}
                className="btn-primary w-full"
                data-testid="support-golfer-btn"
              >
                Support this golfer
              </button>
              <div className="text-xs text-foreground/60 text-center pt-1">
                Monthly support and business sponsorships available inside.
              </div>
              <button onClick={handleFollow} className="text-sm inline-flex items-center justify-center gap-2 py-3 text-foreground/60 hover:text-foreground mt-2" data-testid="athlete-follow-btn">
                <Heart size={14} /> Follow journey
              </button>
            </div>

            {/* SPONSORS */}
            {a.sponsors?.length > 0 && (
              <div className="mt-10">
                <div className="overline mb-4">Current sponsors</div>
                <div className="flex flex-wrap gap-4">
                  {a.sponsors.map((s) => (
                    <div key={s.sponsorship_id} className="border border-border px-4 py-2 text-sm flex items-center gap-2" data-testid={`sponsor-pill-${s.sponsorship_id}`}>
                      <span className="w-1 h-1 bg-[hsl(var(--secondary))]" />
                      <span className="font-medium">{s.company_name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* STORY + BREAKDOWN */}
      <section className="py-20 md:py-28 border-b border-border">
        <div className="container-tight grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <div className="overline">The story</div>
            <h2 className="font-display text-3xl md:text-5xl mt-3 mb-8">A journey, in their own words.</h2>
            <p className="text-base md:text-lg leading-relaxed text-foreground/80 whitespace-pre-line" data-testid="athlete-story">
              {a.story}
            </p>
            <div className="mt-10 flex gap-4">
              {a.social_links?.instagram && (
                <a href={a.social_links.instagram} target="_blank" rel="noreferrer" className="text-sm inline-flex items-center gap-2 hover:text-primary"><Instagram size={14} /> Instagram</a>
              )}
              {a.social_links?.twitter && (
                <a href={a.social_links.twitter} target="_blank" rel="noreferrer" className="text-sm inline-flex items-center gap-2 hover:text-primary"><Twitter size={14} /> Twitter</a>
              )}
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="overline mb-6">Funding breakdown</div>
            <ul className="divide-y divide-border border-y border-border">
              {(a.funding_breakdown || []).map((b, i) => (
                <li key={i} className="py-4 flex items-baseline justify-between" data-testid={`funding-item-${i}`}>
                  <span className="text-sm">{b.label}</span>
                  <span className="font-display text-xl">{fmtEUR(b.amount)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      {a.gallery?.length > 0 && (
        <section className="py-20 md:py-24 border-b border-border bg-muted">
          <div className="container-tight">
            <div className="overline">Gallery</div>
            <h2 className="font-display text-3xl md:text-5xl mt-3 mb-12">In the field.</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {a.gallery.map((g, i) => (
                <div key={i} className="aspect-[4/5] overflow-hidden border border-border" data-testid={`gallery-item-${i}`}>
                  <img src={g} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* UPDATES */}
      {updates.length > 0 && (
        <section className="py-20 md:py-28 border-b border-border">
          <div className="container-tight">
            <div className="overline">Latest updates</div>
            <h2 className="font-display text-3xl md:text-5xl mt-3 mb-12">From the road.</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              {updates.map((u) => (
                <article key={u.update_id} className="border-t border-border pt-6" data-testid={`update-${u.update_id}`}>
                  <div className="overline text-foreground/60">
                    {new Date(u.created_at).toLocaleDateString("en-IE", { year: "numeric", month: "long", day: "numeric" })}
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl mt-2">{u.title}</h3>
                  <p className="mt-3 text-foreground/70 leading-relaxed">{u.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* RECENT DONORS */}
      {donations.length > 0 && (
        <section className="py-20 border-b border-border bg-muted">
          <div className="container-tight">
            <div className="overline">Recent supporters</div>
            <h2 className="font-display text-3xl md:text-5xl mt-3 mb-10">Backed by community.</h2>
            <ul className="divide-y divide-border border-y border-border">
              {donations.slice(0, 8).map((d, i) => (
                <li key={d.donation_id} className="py-4 flex items-baseline justify-between" data-testid={`donor-row-${i}`}>
                  <span className="font-medium">{d.anonymous ? "Anonymous" : d.donor_name}</span>
                  <span className="font-display text-xl">{fmtEUR(d.amount)}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <SupportModal
        open={supportOpen}
        athlete={a}
        initialMode={supportMode}
        initialFrequency={supportFrequency}
        onClose={() => setSupportOpen(false)}
        onSuccess={load}
      />
    </div>
  );
}
